import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Ship, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "h-10 px-3 rounded-xl bg-white border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 w-full";
const Field = ({ label, children }) => <label className="block"><span className="block font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a] mb-1.5">{label}</span>{children}</label>;

/** Delivery tab card: Easyship connection, balance, sender address and parcel defaults. */
export const EasyshipPanel = ({ headers }) => {
  const [st, setSt] = useState(null);
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/admin/easyship/status`, { headers }).then((r) => { setSt(r.data); setD(r.data.defaults); }).catch(() => setSt({ enabled: false, error: "unreachable" })); }, [headers]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/easyship/defaults`, d, { headers });
      setD(r.data);
      toast.success("Parcel defaults saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };
  const num = (path) => (e) => {
    const v = parseFloat(e.target.value);
    setD((x) => (path.length === 1 ? { ...x, [path[0]]: v } : { ...x, box: { ...x.box, [path[1]]: v } }));
  };

  const ok = st?.enabled && st?.ok;
  return (
    <section data-testid="easyship-panel" className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-5 sm:p-6 mt-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] flex items-center gap-2"><Ship size={11} className="text-[#ff2a85]" /> Easyship · labels &amp; tracking</p>
          <p data-testid="easyship-status" className="font-display font-extrabold uppercase text-lg leading-tight mt-1 flex items-center gap-2">
            {!st ? "Checking…" : ok ? <><Check size={16} className="text-[#ff2a85]" /> Connected{st.mode === "sandbox" ? " · sandbox" : ""}</> : <><X size={16} className="text-[#ff2a85]" /> {st.enabled ? "Token rejected" : "Not connected"}</>}
          </p>
          {st && !st.enabled && <p className="text-[11px] text-[#5f5e5a] mt-1">Add your Easyship API token (EASYSHIP_ACCESS_TOKEN) to ship from here. Manual tracking still works.</p>}
          {st?.error && st.enabled && <p className="text-[11px] text-[#ff2a85] mt-1">{st.error}</p>}
          {ok && (
            <p data-testid="easyship-account" className="text-[11px] text-[#5f5e5a] mt-1">
              {st.account} · balance <span className={`font-semibold ${Number(st.balance) <= 0 ? "text-[#ff2a85]" : "text-[#0a0a0b]"}`}>${Number(st.balance || 0).toFixed(2)} {st.currency || ""}</span>
              {st.sender ? ` · ships from ${st.sender.line}` : " · no sender address in Easyship yet"} · {st.webhook ? "tracking webhook active" : "webhook registers on first label"}
            </p>
          )}
          {ok && Number(st.balance) <= 0 && <p className="text-[11px] text-[#ff2a85] mt-1">Top up your Easyship balance before buying labels — purchases fail at $0.</p>}
        </div>
      </div>
      {d && (
        <form onSubmit={save} className="mt-5 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
          <Field label="Weight per item (kg)"><input data-testid="easyship-weight" type="number" step="0.01" min="0.01" value={d.item_weight_kg} onChange={num(["item_weight_kg"])} className={input} /></Field>
          <Field label="Box L (cm)"><input data-testid="easyship-box-l" type="number" step="0.5" min="1" value={d.box.length} onChange={num(["box", "length"])} className={input} /></Field>
          <Field label="Box W (cm)"><input data-testid="easyship-box-w" type="number" step="0.5" min="1" value={d.box.width} onChange={num(["box", "width"])} className={input} /></Field>
          <Field label="Box H (cm)"><input data-testid="easyship-box-h" type="number" step="0.5" min="1" value={d.box.height} onChange={num(["box", "height"])} className={input} /></Field>
          <Field label="Duties"><select data-testid="easyship-incoterms" value={d.incoterms} onChange={(e) => setD({ ...d, incoterms: e.target.value })} className={input}><option value="DDU">Buyer pays (DDU)</option><option value="DDP">We pay (DDP)</option></select></Field>
          <button data-testid="easyship-save" type="submit" disabled={saving} className="h-10 rounded-full bg-[#0a0a0b] hover:bg-[#ff2a85] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 size={12} className="animate-spin" />} Save
          </button>
          <p className="col-span-2 sm:col-span-6 text-[11px] text-[#5f5e5a]">Used to quote couriers and print labels. Weight is multiplied by the number of pieces in the order; label size 4×6 PDF.</p>
        </form>
      )}
    </section>
  );
};
